import React, { useState, useEffect } from "react";
import {
  Moon,
  Sun,
  Menu,
  X,
  ExternalLink,
  Github,
  Linkedin,
  Mail,
  Phone,
  Download,
  Code,
  Database,
  Server,
  Smartphone,
  Award,
  Calendar,
  MapPin,
  ChevronRight,
  Star,
  Users,
  Trophy,
  Target,
  User,
  MessageCircle,
  Send,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const Portfolio = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [animatedElements, setAnimatedElements] = useState(new Set());
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = "Subject must be at least 5 characters";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Send email using EmailJS or similar service
  const sendEmail = async () => {
    try {
      // This is a demo - replace with your actual email service
      const response = await fetch("https://formspree.io/f/mkgvylkz", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
          _subject: `Portfolio Contact: ${formData.subject}`,
        }),
      });

      if (response.ok) {
        return { success: true };
      } else {
        throw new Error("Failed to send email");
      }
    } catch (error) {
      console.error("Email sending failed:", error);
      return { success: false, error: error.message };
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate email sending for demo
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setSubmitStatus({
        type: "success",
        message: "Message sent successfully! I'll get back to you soon.",
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedElements((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll("[id]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "experience", label: "Experience" },
    { id: "skills", label: "Skills" },
    { id: "projects", label: "Projects" },
    { id: "certificates", label: "Certificates" },
    { id: "contact", label: "Contact" },
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  const experiences = [
    {
      title: "Software Developer",
      company: "Exporters Worlds",
      period: "Jul 2023 - Present",
      description:
        "Led end-to-end development of company projects using MERN stack. Implemented admin panel modules for product, order, and user management. Designed RESTful APIs and optimized backend queries. Improved UI/UX with React hooks and reusable components. Provided additional support on Core PHP & MySQL applications.",
      technologies: ["React", "Chart JS", "JavaScript", "PHP", "MySQL", "AI"],
    },
    {
      title: "Developer",
      company: "Era Sky Technologies",
      period: "Apr 2024 - Present",
      description:
        "Core work on MERN stack development – built and optimized full-stack applications using React.js, Node.js, Express, and MongoDB. Developed dynamic dashboards, authentication modules, and payment gateways. Worked on API integrations and enhanced database performance. Also contributed in Core PHP & MySQL projects, maintaining legacy systems. Collaborated with a team of developers to deliver high-performance web apps.",
      technologies: ["MongoDB", "Express.js", "React", "Node.js", "Web3", "AI"],
    },
    {
      title: "Full Stack Developer",
      company: "Permissionless",
      period: "Sep 2022 - Feb 2023",
      description:
        " Developed Web3-integrated solutions using React, Node.js, and MongoDB. Managed deployment pipelines and actively participated in agile sprints. Created and delivered modular UI components specifically for decentralized applications (dApps) and marketplaces.",
      technologies: ["MERN Stack", "Full Stack Development"],
    },
    {
      title: "Freelance Developer",
      period: "2021 - Present",
      description:
        "Delivered small-to-mid scale web apps for clients using React.js, Node.js, and MongoDB. Worked on portfolio websites, e-commerce features, and dashboards. Built REST APIs and responsive UIs.",
      technologies: ["MERN Stack", "Full Stack Development", "PHP", "MySQL"],
    },
  ];

  const skills = {
    frontend: [
      { name: "React.js", level: 95, icon: "⚛️" },
      { name: "Next.js", level: 90, icon: "▲" },
      { name: "Angular", level: 85, icon: "🅰️" },
      { name: "JavaScript", level: 95, icon: "🟨" },
      { name: "HTML/CSS", level: 95, icon: "🌐" },
    ],
    backend: [
      { name: "Node.js", level: 90, icon: "💚" },
      { name: "PHP", level: 88, icon: "🐘" },
      { name: "Laravel", level: 85, icon: "🔴" },
      { name: "Express.js", level: 87, icon: "⚡" },
    ],
    database: [
      { name: "MongoDB", level: 88, icon: "🍃" },
      { name: "MySQL", level: 90, icon: "🐬" },
      { name: "SQL", level: 90, icon: "📊" },
    ],
    other: [
      { name: "Software Testing", level: 80, icon: "🧪" },
      { name: "Web3", level: 75, icon: "🔗" },
      { name: "AI Integration", level: 70, icon: "🤖" },
    ],
  };

  const projects = [
    {
      title: "FoxholeDepot - E-commerce Platform",
      description:
        "Full-fledged e-commerce website with admin panel featuring complete product management, user authentication, payment integration, and comprehensive admin dashboard.",
      technologies: ["PHP", "MySQL"],
      features: [
        "Complete Product Management",
        "User Authentication",
        "Payment Integration",
        "Admin Dashboard",
        "Order Management",
        "Inventory Tracking",
      ],
      status: "Completed",
      image: "/images/foxholedepot-preview.jpg", // Replace with your actual image path
      liveUrl: "https://foxholedepot.exportersworlds.in/", // Replace with your actual live URL
      githubUrl: "https://github.com/ExporterWorlds/foxhole", // Replace with your actual GitHub URL
    },
    {
      title: "ShareMe - Social Media Platform",
      description:
        "Image sharing and social media service designed for seamless photo and infographic sharing experience with visual asset sharing and interactive UI.",
      technologies: ["React.js", "Sanity Database", "Google Authentication"],
      features: [
        "Visual Asset Sharing",
        "Interactive UI",
        "Social Media Functionalities",
        "Google Authentication",
        "Real-time Updates",
        "Responsive Design",
      ],
      status: "Completed",
      image: "/images/shareme-preview.jpg", // Replace with your actual image path
      liveUrl: "https://sharemewithsanity.netlify.app/", // Replace with your actual live URL
      githubUrl: "https://github.com/SAlimAbbas/sharemeFrontend", // Replace with your actual GitHub URL
    },
    {
      title: "Courshera - Course Marketplace",
      description:
        "Course platform where users can buy and resell courses to other users. A comprehensive learning marketplace with user-to-user course trading functionality.",
      technologies: ["React", "Node.js", "MongoDB", "Vercel"],
      features: [
        "Course Marketplace",
        "Buy & Resell System",
        "User Dashboard",
        "Payment Integration",
        "Course Management",
        "User Authentication",
      ],
      status: "Completed",
      image: "/images/courshera-preview.jpg", // Replace with your actual image path
      liveUrl: "https://courshareapp.netlify.app/", // Replace with your actual live URL
      githubUrl: "https://github.com/SAlimAbbas/Aces", // Replace with your actual GitHub URL
    },
  ];

  const achievements = [
    { icon: Trophy, label: "Projects Completed", value: "25+" },
    { icon: Users, label: "Happy Clients", value: "15+" },
    { icon: Star, label: "Code Quality", value: "98%" },
    { icon: Target, label: "Success Rate", value: "99%" },
  ];

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        darkMode ? "dark bg-gray-900" : "bg-white"
      }`}
    >
      {/* Navigation */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          darkMode
            ? "bg-gray-900/90 border-gray-800"
            : "bg-white/90 border-gray-200"
        } backdrop-blur-md border-b`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div
              className={`text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}
            >
              Syed Alim Abbas
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`transition-colors duration-200 ${
                    activeSection === item.id
                      ? "text-blue-600 dark:text-blue-400"
                      : darkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Theme Toggle & Mobile Menu */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode
                    ? "bg-gray-800 hover:bg-gray-700"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-lg transition-colors ${
                  darkMode
                    ? "bg-gray-800 hover:bg-gray-700"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className={`md:hidden border-t ${
              darkMode
                ? "bg-gray-900 border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    activeSection === item.id
                      ? "bg-blue-600 text-white"
                      : darkMode
                      ? "text-gray-300 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="pt-16 min-h-screen flex flex-col relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20"></div>

        {/* Main content area - flex-1 to take available space */}
        <div className="flex-1 flex items-center relative z-10 pb-14 md:pb-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div
                className={`space-y-8 order-2 lg:order-1 ${
                  animatedElements.has("home")
                    ? "animate-fade-in-up"
                    : "opacity-0"
                }`}
              >
                <div className="space-y-4">
                  <p className="text-lg text-blue-600 dark:text-blue-400 font-medium">
                    Hello, I Am
                  </p>
                  <h1
                    className={`text-4xl md:text-5xl lg:text-6xl font-bold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Syed Alim Abbas
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-lg leading-relaxed">
                    Full-Stack Developer specializing in MERN and MEAN stacks,
                    passionate about building scalable, high-performance web
                    applications and emerging technologies.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => scrollToSection("contact")}
                    className="px-6 md:px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 font-medium"
                  >
                    Get In Touch <ChevronRight size={20} />
                  </button>
                  <button
                    onClick={() =>
                      window.open("/files/Syed-Alim-Abbas-Resume.pdf", "_blank")
                    }
                    className={`px-6 md:px-8 py-3 border-2 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium ${
                      darkMode
                        ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Download size={20} /> Download CV
                  </button>
                </div>

                <div className="flex items-center gap-6 justify-center sm:justify-start">
                  <a
                    href="mailto:alimabbas44891@gmail.com"
                    className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors transform hover:scale-110"
                  >
                    <Mail size={24} />
                  </a>
                  <a
                    href="https://www.linkedin.com/in/syedalimabbas/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors transform hover:scale-110"
                  >
                    <Linkedin size={24} />
                  </a>
                  <a
                    href="https://github.com/SAlimAbbas"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors transform hover:scale-110"
                  >
                    <Github size={24} />
                  </a>
                </div>
              </div>

              <div className="relative order-1 lg:order-2 flex justify-center">
                <div className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 mx-auto relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-pulse opacity-20"></div>
                  <div
                    className={`w-full h-full rounded-full border-4 ${
                      darkMode ? "border-gray-700" : "border-white"
                    } shadow-2xl overflow-hidden relative z-10 transform hover:scale-105 transition-transform duration-300`}
                  >
                    <img
                      src="/images/profile.jpg"
                      alt="Syed Alim Abbas - Full Stack Developer"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <div
                      className={`w-full h-full ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      } rounded-full items-center justify-center text-4xl font-bold ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      } hidden`}
                      style={{ display: "none" }}
                    >
                      SA
                    </div>
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-bounce delay-1000"></div>
                  <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full animate-bounce delay-500"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Achievements Bar - positioned at bottom but not overlapping */}
        <div className="relative mt-auto">
          <div
            className={`${
              darkMode ? "bg-gray-800/90" : "bg-white/90"
            } backdrop-blur-md border-t ${
              darkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {achievements.map((achievement, index) => (
                  <div key={index} className="text-center">
                    <div className="flex justify-center mb-2">
                      <achievement.icon
                        className="text-blue-600 dark:text-blue-400"
                        size={20}
                      />
                    </div>
                    <div
                      className={`text-xl md:text-2xl font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {achievement.value}
                    </div>
                    <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                      {achievement.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 ${
              animatedElements.has("about") ? "animate-fade-in-up" : "opacity-0"
            }`}
          >
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              About Me
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Passionate full-stack developer with expertise in modern web
              technologies and a drive for continuous learning.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div
                className={`p-6 rounded-2xl ${
                  darkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3
                  className={`text-2xl font-bold mb-4 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  My Journey
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  I'm a full-stack developer with expertise in MERN and MEAN
                  stacks, passionate about building scalable, high-performance
                  web applications. My skills span across modern JavaScript
                  frameworks like React.js, Next.js, Node.js, and MongoDB, along
                  with core knowledge in PHP and MySQL.
                </p>
              </div>

              <div
                className={`p-6 rounded-2xl ${
                  darkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3
                  className={`text-2xl font-bold mb-4 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  What I Do
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Over the years, I've successfully delivered multiple
                  end-to-end projects—from architecture design to
                  deployment—serving real business needs and user-centric goals.
                  I thrive on solving complex technical challenges, optimizing
                  performance, and adhering to strict deadlines.
                </p>
              </div>

              <div
                className={`p-6 rounded-2xl ${
                  darkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3
                  className={`text-2xl font-bold mb-4 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Future Focus
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  I'm continuously upgrading my skills in AI, Web3, and emerging
                  technologies to stay ahead of the curve. My goal is to
                  contribute to innovative projects that make a meaningful
                  impact.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div
                className={`p-8 rounded-2xl ${
                  darkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3
                  className={`text-2xl font-bold mb-6 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Personal Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Mail
                      className="text-blue-600 dark:text-blue-400"
                      size={20}
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Email
                      </p>
                      <p className="text-gray-600 dark:text-gray-300">
                        alimabbas44891@gmail.com
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Phone
                      className="text-blue-600 dark:text-blue-400"
                      size={20}
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Phone
                      </p>
                      <p className="text-gray-600 dark:text-gray-300">
                        +91 95694 72191
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <MapPin
                      className="text-blue-600 dark:text-blue-400"
                      size={20}
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Location
                      </p>
                      <p className="text-gray-600 dark:text-gray-300">India</p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`p-8 rounded-2xl ${
                  darkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3
                  className={`text-2xl font-bold mb-6 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Education
                </h3>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Bachelor of Computer Applications (BCA)
                    </h4>
                    <p className="text-blue-600 dark:text-blue-400">
                      Indira Gandhi National Open University
                    </p>
                    <p className="text-gray-600 dark:text-gray-300">
                      2020 - 2024
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section
        id="experience"
        className={`py-20 ${darkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 ${
              animatedElements.has("experience")
                ? "animate-fade-in-up"
                : "opacity-0"
            }`}
          >
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Work Experience
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              My professional journey building innovative solutions and
              contributing to amazing projects.
            </p>
          </div>

          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className={`${
                  darkMode ? "bg-gray-800" : "bg-white"
                } rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <div className="flex flex-wrap justify-between items-start mb-6">
                  <div>
                    <h3
                      className={`text-2xl font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {exp.title}
                    </h3>
                    <p className="text-blue-600 dark:text-blue-400 text-lg font-medium">
                      {exp.company}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Calendar size={16} />
                    <span>{exp.period}</span>
                  </div>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {exp.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {exp.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className={`px-3 py-1 rounded-full text-sm ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 ${
              animatedElements.has("skills")
                ? "animate-fade-in-up"
                : "opacity-0"
            }`}
          >
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Skills & Expertise
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              A comprehensive overview of my technical skills and proficiency
              levels.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {Object.entries(skills).map(
              ([category, skillList], categoryIndex) => (
                <div
                  key={category}
                  className={`${
                    darkMode ? "bg-gray-800" : "bg-white"
                  } rounded-2xl p-8 shadow-lg`}
                >
                  <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                      {category === "frontend" && (
                        <Code className="text-white" size={24} />
                      )}
                      {category === "backend" && (
                        <Server className="text-white" size={24} />
                      )}
                      {category === "database" && (
                        <Database className="text-white" size={24} />
                      )}
                      {category === "other" && (
                        <Smartphone className="text-white" size={24} />
                      )}
                    </div>
                    <h3
                      className={`text-2xl font-bold capitalize ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {category === "other" ? "Other Skills" : category}
                    </h3>
                  </div>

                  <div className="space-y-6">
                    {skillList.map((skill, skillIndex) => (
                      <div key={skillIndex}>
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{skill.icon}</span>
                            <span
                              className={`font-medium ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {skill.name}
                            </span>
                          </div>
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            {skill.level}%
                          </span>
                        </div>
                        <div
                          className={`h-2 rounded-full ${
                            darkMode ? "bg-gray-700" : "bg-gray-200"
                          }`}
                        >
                          <div
                            className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all duration-1000 ease-out"
                            style={{
                              width: animatedElements.has("skills")
                                ? `${skill.level}%`
                                : "0%",
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section
        id="projects"
        className={`py-20 ${darkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 ${
              animatedElements.has("projects")
                ? "animate-fade-in-up"
                : "opacity-0"
            }`}
          >
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              My Work
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              A showcase of my recent work and the technologies I've been
              working with.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`${
                  darkMode ? "bg-gray-800" : "bg-white"
                } rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
              >
                <div className="h-48 bg-gradient-to-br relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover opacity-70"
                    />
                  </div>
                  <div className="absolute top-4 right-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        project.status === "Completed"
                          ? "bg-green-500 text-white"
                          : "bg-yellow-500 text-black"
                      }`}
                    >
                      {project.status}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3
                    className={`text-xl font-bold mb-3 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {project.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  <div className="mb-4">
                    <h4
                      className={`font-medium mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Key Features:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {project.features.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className={`px-2 py-1 rounded text-xs ${
                            darkMode
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4
                      className={`font-medium mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Technologies:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => window.open(project.liveUrl, "_blank")}
                      className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 text-sm`}
                    >
                      <ExternalLink size={16} />
                      Live Demo
                    </button>
                    <button
                      onClick={() => window.open(project.githubUrl, "_blank")}
                      className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-all duration-200 text-sm ${
                        darkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Github size={16} />
                      Code
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certificates Section */}
      <section id="certificates" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 ${
              animatedElements.has("certificates")
                ? "animate-fade-in-up"
                : "opacity-0"
            }`}
          >
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Certificates & Achievements
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Professional certifications and educational achievements that
              validate my expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div
              className={`${
                darkMode ? "bg-gray-800" : "bg-white"
              } rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <div className="flex items-start gap-6">
                <div className="p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl">
                  <Award className="text-white" size={32} />
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-2xl font-bold mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Masai Bootcamp Graduation
                  </h3>
                  <p className="text-blue-600 dark:text-blue-400 font-medium mb-3">
                    Full Stack Web Development Program
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Completed 2160 hours of practical learning in Masai's Full
                    Stack Web Development program, covering modern web
                    technologies and best practices.
                  </p>
                  <div className="flex items-center gap-4">
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      2160 Hours
                    </span>
                    <button
                      onClick={() =>
                        (window.location.href =
                          "https://drive.google.com/file/d/1LgaMnBQPgxfmiNqNMvH-06kw71JkYoJ0/view?usp=sharing")
                      }
                      target="_blank"
                      className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      View Certificate <ExternalLink size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`${
                darkMode ? "bg-gray-800" : "bg-white"
              } rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <div className="flex items-start gap-6">
                <div className="p-4 bg-gradient-to-r from-green-600 to-teal-600 rounded-xl">
                  <Award className="text-white" size={32} />
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-2xl font-bold mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    BCA Degree
                  </h3>
                  <p className="text-green-600 dark:text-green-400 font-medium mb-3">
                    Bachelor of Computer Applications
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Graduated with a BCA degree from Indira Gandhi National Open
                    University, providing strong foundation in computer science
                    principles and practical skills.
                  </p>
                  <div className="flex items-center gap-4">
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      2020 - 2024
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      IGNOU
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Certifications Placeholder */}
          <div className="mt-12 text-center">
            <div
              className={`inline-flex items-center gap-3 px-6 py-3 rounded-xl ${
                darkMode
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-gray-50 border border-gray-200"
              }`}
            >
              <Award className="text-blue-600 dark:text-blue-400" size={20} />
              <span className="text-gray-600 dark:text-gray-300">
                More certifications in Web3, AI, and Cloud Technologies coming
                soon!
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className={`py-20 ${darkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 ${
              animatedElements.has("contact")
                ? "animate-fade-in-up"
                : "opacity-0"
            }`}
          >
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Let's discuss how we can work together to bring your ideas to
              life.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div
                className={`${
                  darkMode ? "bg-gray-800" : "bg-white"
                } rounded-2xl p-8 shadow-lg`}
              >
                <h3
                  className={`text-2xl font-bold mb-6 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Let's Connect
                </h3>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                      <Mail className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Email
                      </p>
                      <a
                        href="mailto:alimabbas44891@gmail.com"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        alimabbas44891@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-r from-green-600 to-teal-600 rounded-lg">
                      <Phone className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Phone
                      </p>
                      <a
                        href="tel:+919569472191"
                        className="text-green-600 dark:text-green-400 hover:underline"
                      >
                        +91 95694 72191
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
                      <Linkedin className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        LinkedIn
                      </p>
                      <a
                        href="https://www.linkedin.com/in/syedalimabbas/"
                        target="_blank"
                        className="text-purple-600 dark:text-purple-400 hover:underline"
                      >
                        Connect with me
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`${
                  darkMode ? "bg-gray-800" : "bg-white"
                } rounded-2xl p-8 shadow-lg`}
              >
                <h3
                  className={`text-2xl font-bold mb-4 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Available For
                </h3>
                <div className="space-y-3">
                  {[
                    "Full-stack Web Development",
                    "MERN/MEAN Stack Projects",
                    "Web3 & AI Integration",
                    "Consulting & Code Review",
                    "Freelance Projects",
                  ].map((service, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-300">
                        {service}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div
              className={`${
                darkMode ? "bg-gray-800" : "bg-white"
              } rounded-2xl p-8 shadow-lg`}
            >
              <h3
                className={`text-2xl font-bold mb-6 flex items-center gap-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <MessageCircle size={24} />
                Send Message
              </h3>

              {/* Status Message */}
              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
                    submitStatus.type === "success"
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {submitStatus.type === "success" ? (
                    <CheckCircle size={20} />
                  ) : (
                    <AlertCircle size={20} />
                  )}
                  {submitStatus.message}
                </div>
              )}

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      First Name *
                    </label>
                    <div className="relative">
                      <User
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.firstName
                            ? "border-red-500 focus:ring-red-500"
                            : darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="John"
                      />
                    </div>
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Last Name *
                    </label>
                    <div className="relative">
                      <User
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.lastName
                            ? "border-red-500 focus:ring-red-500"
                            : darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="Doe"
                      />
                    </div>
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Email *
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.email
                          ? "border-red-500 focus:ring-red-500"
                          : darkMode
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                      placeholder="john@example.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.subject
                        ? "border-red-500 focus:ring-red-500"
                        : darkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="Project Discussion"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                      errors.message
                        ? "border-red-500 focus:ring-red-500"
                        : darkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="Tell me about your project..."
                  ></textarea>
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 ${
                    isSubmitting
                      ? "opacity-70 cursor-not-allowed transform-none"
                      : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className={`py-8 border-t ${
          darkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Syed Alim Abbas
            </div>

            <div
              className={`text-center ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              <p>© 2024 Syed Alim Abbas. All rights reserved.</p>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="mailto:alimabbas44891@gmail.com"
                className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Mail size={20} />
              </a>
              <a
                href="#"
                className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="#"
                className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Github size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: ${darkMode ? "#1f2937" : "#f1f5f9"};
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #7c3aed);
        }
      `}</style>
    </div>
  );
};

export default Portfolio;
